import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { MailPlus } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { invitationListColumns, memberListColumns, type ListQuery } from "@patronix/domain/list";
import { DataList, DetailPanel, DetailPlaceholder, type Column } from "@/components/DataList";
import { Badge } from "@/components/ui";
import {
  invitationListQuery,
  invitationState,
  memberListQuery,
  roleListQuery,
  type InvitationRow,
  type MemberRow,
} from "@/features/users/api";
import { InvitationPanel, invitationTone, InvitePanel, MemberPanel, roleNames } from "@/features/users/UserPanels";
import { filteredQuery, type ClientColumn } from "@/lib/clientList";
import { cn } from "@/lib/cn";
import { useFormat } from "@/lib/format";
import { useCan } from "@/lib/session";

// Settings → Users: members of the organization and invitations (ADR-021).
// Round-trips: members RPC + roles (cached 5 min) on open; invitations when
// that tab opens. Each list is one page (organizations have ≤ hundreds of
// members), virtualized by DataList. Detail panels use the list rows, so
// selecting a row costs no request.

type Tab = "members" | "invitations";

interface Search {
  tab: Tab;
  /** member user_id, invitation id, or "new" for the invite form */
  sel?: string | undefined;
}

export const Route = createFileRoute("/_app/settings/users")({
  validateSearch: (raw: Record<string, unknown>): Search => {
    const s: Search = { tab: raw.tab === "invitations" ? "invitations" : "members" };
    if (typeof raw.sel === "string" && raw.sel && raw.sel.length <= 64) s.sel = raw.sel;
    return s;
  },
  loaderDeps: ({ search }) => ({ tab: search.tab }),
  loader: ({ context, deps }) =>
    Promise.all([
      deps.tab === "members"
        ? context.queryClient.ensureInfiniteQueryData(memberListQuery())
        : context.queryClient.ensureInfiniteQueryData(invitationListQuery()),
      context.queryClient.ensureQueryData(roleListQuery()),
    ]),
  component: UsersPage,
});

function UsersPage() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const canManage = useCan("core.user.manage");
  const setSearch = useCallback(
    (patch: Partial<Search>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );

  const inviteButton = (className: string) => (
    <button type="button" onClick={() => setSearch({ sel: "new" })} className={className}>
      <MailPlus size={17} strokeWidth={2} aria-hidden="true" />
      {t("users.invite")}
    </button>
  );

  return (
    <div className="flex min-h-0 grow flex-col gap-4">
      <header className="flex flex-wrap items-end gap-3">
        <div className="flex grow flex-col gap-0.5">
          <nav aria-label="Breadcrumb" className="text-[13px] text-ink-muted">
            {t("nav.settingsGroup.title")} <span aria-hidden="true">/</span> {t("nav.settingsGroup.users")}
          </nav>
          <h1 className="text-[28px] font-semibold leading-9 tracking-[-0.02em]">{t("users.title")}</h1>
        </div>
        {canManage &&
          inviteButton("inline-flex h-10 items-center gap-1.5 rounded-xl bg-accent pl-3 pr-4 font-medium text-on-accent hover:bg-accent-hover")}
      </header>

      <div role="tablist" aria-label={t("users.title")} className="flex gap-1 border-b border-border">
        {(["members", "invitations"] as const).map((tab) => (
          <button
            key={tab}
            type="button"
            role="tab"
            aria-selected={search.tab === tab}
            onClick={() => setSearch({ tab, sel: undefined })}
            className={cn(
              "-mb-px h-9 border-b-2 px-3 text-[13px] font-medium",
              search.tab === tab ? "border-accent text-accent-ink" : "border-transparent text-ink-muted hover:text-ink",
            )}
          >
            {t(`users.tab.${tab}`)}
          </button>
        ))}
      </div>

      {search.tab === "members" ? (
        <MembersTab sel={search.sel} setSearch={setSearch} inviteButton={canManage ? inviteButton : undefined} />
      ) : (
        <InvitationsTab sel={search.sel} setSearch={setSearch} inviteButton={canManage ? inviteButton : undefined} />
      )}
    </div>
  );
}

type TabProps = {
  sel: string | undefined;
  setSearch: (p: Partial<Search>) => void;
  inviteButton: ((className: string) => React.ReactNode) | undefined;
};

const smallButton =
  "inline-flex h-9 items-center gap-1 rounded-xl bg-accent px-3.5 text-[13px] font-medium text-on-accent hover:bg-accent-hover";

function InviteDetail({ setSearch }: { setSearch: (p: Partial<Search>) => void }) {
  return (
    <InvitePanel
      onClose={() => setSearch({ sel: undefined })}
      onInvited={(id) => setSearch({ tab: "invitations", sel: id })}
    />
  );
}

function MembersTab({ sel, setSearch, inviteButton }: TabProps) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const roles = useQuery(roleListQuery());
  // One page, filtered and sorted in the browser; the view is local to the tab.
  const [listQuery, setListQuery] = useState<ListQuery>({ filters: [], sort: null });
  const clientColumns = useMemo<ClientColumn<MemberRow>[]>(
    () => [
      { key: "name", type: "text", value: (r) => r.full_name ?? r.email },
      { key: "roles", type: "text", value: (r) => roleNames(roles.data, r.role_ids) },
      { key: "status", type: "enum", value: (r) => r.status },
      { key: "last", type: "date", value: (r) => r.last_sign_in_at },
    ],
    [roles.data],
  );
  const loaded = useInfiniteQuery(memberListQuery());
  const rows = useMemo(() => loaded.data?.pages.flatMap((p) => p.rows) ?? [], [loaded.data]);
  const query = filteredQuery(loaded, clientColumns, listQuery.filters, listQuery.sort);
  const selected = sel && sel !== "new" ? rows.find((r) => r.user_id === sel) : undefined;

  const columns: Column<MemberRow>[] = [
    {
      key: "name",
      header: t("users.colName"),
      width: "minmax(200px,1.6fr)",
      cell: (r, isSel) => (
        <span className="flex min-w-0 flex-col">
          <span className={cn("truncate", isSel && "font-semibold text-accent-ink", r.status === "disabled" && "text-ink-muted line-through decoration-ink-faint")}>
            {r.full_name ?? r.email}
          </span>
          <span className="truncate text-xs text-ink-muted">{r.email}</span>
        </span>
      ),
    },
    {
      key: "roles",
      header: t("users.roles"),
      width: "minmax(160px,1.2fr)",
      className: "text-ink-soft",
      cell: (r) => <span className="truncate">{roleNames(roles.data, r.role_ids) || "—"}</span>,
    },
    {
      key: "status",
      header: t("users.colStatus"),
      width: "minmax(90px,0.6fr)",
      cell: (r) => (
        <Badge tone={r.status === "active" ? "success" : "neutral"}>{t(r.status === "active" ? "users.statusActive" : "users.statusDisabled")}</Badge>
      ),
      options: [
        { value: "active", label: t("users.statusActive") },
        { value: "disabled", label: t("users.statusDisabled") },
      ],
    },
    {
      key: "last",
      header: t("users.lastSignIn"),
      width: "minmax(120px,0.8fr)",
      className: "tabular text-ink-soft",
      cell: (r) => (r.last_sign_in_at ? fmt.dateTime(r.last_sign_in_at) : t("users.never")),
    },
  ];

  return (
    <div className="flex min-h-0 grow gap-4">
      <DataList
        label={t("users.tab.members")}
        columns={columns}
        query={query}
        rowId={(r) => r.user_id}
        selectedId={sel}
        onSelect={(r) => setSearch({ sel: r.user_id })}
        onClear={() => setSearch({ sel: undefined })}
        emptyTitle={t("users.emptyMembersTitle")}
        emptyBody={t("users.emptyMembersBody")}
        emptyAction={inviteButton?.(smallButton)}
        minWidth={640}
        tools={{
          id: "users-members",
          title: t("users.tab.members"),
          onNew: inviteButton ? () => setSearch({ sel: "new" }) : undefined,
          newLabel: t("users.invite"),
        }}
        view={{ spec: memberListColumns, query: listQuery, onChange: setListQuery }}
      />
      <DetailPanel
        label={t("users.detailLabel")}
        open={!!sel}
        placeholder={<DetailPlaceholder title={t("users.selectPrompt")} hint={t("users.selectPromptHint")} />}
      >
        {sel === "new" ? (
          <InviteDetail setSearch={setSearch} />
        ) : selected ? (
          <MemberPanel key={selected.user_id} member={selected} onClose={() => setSearch({ sel: undefined })} />
        ) : null}
      </DetailPanel>
    </div>
  );
}

function InvitationsTab({ sel, setSearch, inviteButton }: TabProps) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const roles = useQuery(roleListQuery());
  const [listQuery, setListQuery] = useState<ListQuery>({ filters: [], sort: null });
  const clientColumns = useMemo<ClientColumn<InvitationRow>[]>(
    () => [
      { key: "email", type: "text", value: (r) => `${r.email} ${r.full_name ?? ""}` },
      { key: "role", type: "text", value: (r) => roleNames(roles.data, [r.role_id]) },
      { key: "status", type: "enum", value: (r) => invitationState(r) },
      { key: "created", type: "date", value: (r) => r.created_at },
    ],
    [roles.data],
  );
  const loaded = useInfiniteQuery(invitationListQuery());
  const rows = useMemo(() => loaded.data?.pages.flatMap((p) => p.rows) ?? [], [loaded.data]);
  const query = filteredQuery(loaded, clientColumns, listQuery.filters, listQuery.sort);
  const selected = sel && sel !== "new" ? rows.find((r) => r.id === sel) : undefined;

  const columns: Column<InvitationRow>[] = [
    {
      key: "email",
      header: t("users.email"),
      width: "minmax(220px,1.6fr)",
      cell: (r, isSel) => (
        <span className="flex min-w-0 flex-col">
          <span className={cn("truncate", isSel && "font-semibold text-accent-ink")}>{r.email}</span>
          {r.full_name && <span className="truncate text-xs text-ink-muted">{r.full_name}</span>}
        </span>
      ),
    },
    {
      key: "role",
      header: t("users.role"),
      width: "minmax(140px,1fr)",
      className: "text-ink-soft",
      cell: (r) => <span className="truncate">{roleNames(roles.data, [r.role_id]) || "—"}</span>,
    },
    {
      key: "status",
      header: t("users.colStatus"),
      width: "minmax(100px,0.7fr)",
      cell: (r) => {
        const state = invitationState(r);
        return <Badge tone={invitationTone[state]}>{t(`users.inv.${state}`)}</Badge>;
      },
      options: (["pending", "expired", "accepted", "revoked"] as const).map((v) => ({ value: v, label: t(`users.inv.${v}`) })),
    },
    {
      key: "created",
      header: t("users.invited"),
      width: "minmax(120px,0.8fr)",
      className: "tabular text-ink-soft",
      cell: (r) => fmt.dateTime(r.created_at),
    },
  ];

  return (
    <div className="flex min-h-0 grow gap-4">
      <DataList
        label={t("users.tab.invitations")}
        columns={columns}
        query={query}
        rowId={(r) => r.id}
        selectedId={sel}
        onSelect={(r) => setSearch({ sel: r.id })}
        onClear={() => setSearch({ sel: undefined })}
        emptyTitle={t("users.emptyInvitationsTitle")}
        emptyBody={t("users.emptyInvitationsBody")}
        emptyAction={inviteButton?.(smallButton)}
        minWidth={640}
        tools={{
          id: "users-invitations",
          title: t("users.tab.invitations"),
          onNew: inviteButton ? () => setSearch({ sel: "new" }) : undefined,
          newLabel: t("users.invite"),
        }}
        view={{ spec: invitationListColumns, query: listQuery, onChange: setListQuery }}
      />
      <DetailPanel
        label={t("users.detailLabel")}
        open={!!sel}
        placeholder={<DetailPlaceholder title={t("users.selectInvitation")} hint={t("users.selectPromptHint")} />}
      >
        {sel === "new" ? (
          <InviteDetail setSearch={setSearch} />
        ) : selected ? (
          <InvitationPanel key={selected.id} invitation={selected} onClose={() => setSearch({ sel: undefined })} />
        ) : null}
      </DetailPanel>
    </div>
  );
}
